import React, { useCallback, useMemo, useState } from 'react';
import { toast as message } from 'sonner';
import { supabaseClient } from '../../utils/supabase';
import { useGlobalMetadata } from '../../utils/providers';
import EnhancedDialog from '../EnhancedDialog';

/**
 * REG-707: change one field on every ticked submission. Shared by the
 * Submissions list (report grid) and the workflow submenus.
 */
export type BulkFieldKey = 'status' | 'reason_for_decline' | 'missing_documents' | 'note' | 'amount' | 'factor_rate' | 'term' | 'commission_percent';
type BulkInputType = 'picklist' | 'textarea' | 'number';

interface BulkField {
  key: BulkFieldKey;
  label: string;
  input: BulkInputType;
  step?: string;
  min?: number;
}

interface BulkPicklistOption {
  value: string;
  label?: string;
  place?: number;
}

interface BulkFieldMetadata {
  is_system?: boolean;
  is_calculated?: boolean;
  is_required?: boolean;
  picklist?: { values?: BulkPicklistOption[] };
}

interface SubmissionFieldMetadata {
  fieldData?: { submission?: { byFieldName?: Record<string, BulkFieldMetadata> } };
  tableData?: { byName?: { submission?: { schema_name?: string } } };
}

interface SubmissionUpdateClient {
  schema(schema: string): {
    from(table: 'submission'): {
      select(columns: string): {
        in(column: string, values: number[]): Promise<{ data: Array<{ id: number } & Partial<Record<BulkFieldKey, unknown>>> | null; error: Error | null }>;
      };
      update(values: Partial<Record<BulkFieldKey, string | number | null>>): {
        in(column: string, values: number[]): {
          select(columns: string): Promise<{ data: Array<{ id: number }> | null; error: Error | null }>;
        };
      };
    };
  };
}

const EMPTY_BULK_FIELD_METADATA: Record<string, BulkFieldMetadata> = {};

const BULK_EDIT_FIELDS: BulkField[] = [
  { key: 'status', label: 'Status', input: 'picklist' },
  { key: 'reason_for_decline', label: 'Decline Reason', input: 'picklist' },
  { key: 'missing_documents', label: 'Missing Documents', input: 'textarea' },
  { key: 'note', label: 'Note', input: 'textarea' },
  { key: 'amount', label: 'Approval Amount', input: 'number', step: '0.01', min: 0 },
  { key: 'factor_rate', label: 'Factor Rate', input: 'number', step: '0.001', min: 0 },
  { key: 'term', label: 'Term', input: 'number', step: '1', min: 0 },
  { key: 'commission_percent', label: 'Commission %', input: 'number', step: '0.01', min: 0 },
];

interface SubmissionBulkEditProps {
  /** The ticked submissions (only their ids are used). */
  selectedIds: number[];
  onClearSelection: () => void;
  /**
   * After a successful write: the field, its new value, the ids written and
   * each one's value before the edit (for Undo).
   */
  onSaved: (field: BulkFieldKey, value: string | number | null, ids: Set<number>, previous: Map<number, unknown>) => void;
  /** The page offers Undo for this edit (the confirmation says so). */
  undoable?: boolean;
}

/** "Clear selection" and "Edit N selected" for the toolbar, plus the edit dialog. */
export function SubmissionBulkEdit({ selectedIds, onClearSelection, onSaved, undoable = false }: SubmissionBulkEditProps): React.ReactElement | null {
  const { data: metadata } = useGlobalMetadata();
  const [bulkEditorOpen, setBulkEditorOpen] = useState(false);
  const [bulkFieldKey, setBulkFieldKey] = useState<BulkFieldKey>('status');
  const [bulkValue, setBulkValue] = useState('');
  const [clearValue, setClearValue] = useState(false);
  const [confirmingBulkEdit, setConfirmingBulkEdit] = useState(false);
  const [savingBulkEdit, setSavingBulkEdit] = useState(false);

  const fieldMetadata = useMemo(
    () => (metadata as unknown as SubmissionFieldMetadata | undefined)?.fieldData?.submission?.byFieldName || EMPTY_BULK_FIELD_METADATA,
    [metadata],
  );
  const schema = (metadata as unknown as SubmissionFieldMetadata | undefined)?.tableData?.byName?.submission?.schema_name || 'public';
  const bulkFields = useMemo(
    () => BULK_EDIT_FIELDS.filter((field) => {
      const metadataField = fieldMetadata[field.key];
      return metadataField && !metadataField.is_system && !metadataField.is_calculated;
    }),
    [fieldMetadata],
  );
  const selectedBulkField = bulkFields.find((field) => field.key === bulkFieldKey) || bulkFields[0];
  const selectedFieldMetadata = selectedBulkField ? fieldMetadata[selectedBulkField.key] : null;
  const picklistValues = useMemo(() => {
    const values = selectedFieldMetadata?.picklist?.values;
    return Array.isArray(values) ? [...values].sort((a, b) => (a.place ?? 0) - (b.place ?? 0)) : [];
  }, [selectedFieldMetadata]);
  const count = selectedIds.length;

  const closeBulkEditor = useCallback(() => {
    if (savingBulkEdit) return;
    setBulkEditorOpen(false);
    setConfirmingBulkEdit(false);
    setBulkValue('');
    setClearValue(false);
  }, [savingBulkEdit]);

  const openBulkEditor = useCallback(() => {
    const firstField = bulkFields[0];
    if (!firstField) {
      message.error('No editable submission fields are available.');
      return;
    }
    setBulkFieldKey(firstField.key);
    setBulkValue('');
    setClearValue(false);
    setConfirmingBulkEdit(false);
    setBulkEditorOpen(true);
  }, [bulkFields]);

  const resetBulkField = useCallback((nextFieldKey: BulkFieldKey) => {
    setBulkFieldKey(nextFieldKey);
    setBulkValue('');
    setClearValue(false);
    setConfirmingBulkEdit(false);
  }, []);

  const getBulkValueError = useCallback((): string | null => {
    if (!selectedBulkField) return 'Choose a field to update.';
    if (clearValue) return selectedFieldMetadata?.is_required ? `${selectedBulkField.label} is required and cannot be cleared.` : null;
    if (selectedBulkField.input === 'picklist' && picklistValues.length === 0) return `The ${selectedBulkField.label} options are still loading.`;
    if (!bulkValue.trim()) return `Enter a value for ${selectedBulkField.label}.`;
    if (selectedBulkField.input === 'number') {
      const numberValue = Number(bulkValue);
      if (!Number.isFinite(numberValue)) return `${selectedBulkField.label} must be a valid number.`;
      if (selectedBulkField.min != null && numberValue < selectedBulkField.min) return `${selectedBulkField.label} cannot be negative.`;
    }
    return null;
  }, [bulkValue, clearValue, picklistValues.length, selectedBulkField, selectedFieldMetadata]);

  const saveBulkEdit = useCallback(async () => {
    const valueError = getBulkValueError();
    if (valueError) {
      message.error(valueError);
      return;
    }
    if (!selectedBulkField || selectedIds.length === 0) return;

    const value: string | number | null = clearValue
      ? null
      : selectedBulkField.input === 'number'
        ? Number(bulkValue)
        : bulkValue.trim();
    const ids = [...selectedIds];
    setSavingBulkEdit(true);
    try {
      const submissionClient = supabaseClient as unknown as SubmissionUpdateClient;
      // The values before the edit, so Undo can put each row back.
      const { data: before, error: readError } = await submissionClient
        .schema(schema)
        .from('submission')
        .select(`id,${selectedBulkField.key}`)
        .in('id', ids);
      if (readError) throw readError;
      const previous = new Map<number, unknown>((before || []).map((row) => [row.id, row[selectedBulkField.key] ?? null]));
      const { data, error } = await submissionClient
        .schema(schema)
        .from('submission')
        .update({ [selectedBulkField.key]: value })
        .in('id', ids)
        .select('id');
      if (error) throw error;
      if ((data || []).length !== ids.length) {
        throw new Error('Some selected submissions could not be updated. You may not have permission to edit them.');
      }
      onSaved(selectedBulkField.key, value, new Set(ids), previous);
      onClearSelection();
      setBulkEditorOpen(false);
      setConfirmingBulkEdit(false);
      setBulkValue('');
      setClearValue(false);
      message.success(`Updated ${ids.length} submission${ids.length === 1 ? '' : 's'}.`);
    } catch (error) {
      message.error((error as Error).message || 'Unable to update the selected submissions.');
    } finally {
      setSavingBulkEdit(false);
    }
  }, [bulkValue, clearValue, getBulkValueError, onClearSelection, onSaved, schema, selectedBulkField, selectedIds]);

  return (
    <>
      {count > 0 && (
        <>
          <button type="button" className="submission-clear-selection" onClick={onClearSelection}>Clear selection</button>
          <button
            type="button"
            className="sky-btn sky-btn-ghost submission-edit-selected"
            disabled={!bulkFields.length}
            title={!bulkFields.length
              ? 'No editable submission fields are available.'
              : `Change one field on the ${count} selected submission${count === 1 ? '' : 's'}.`}
            onClick={openBulkEditor}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
            Edit {count} selected
          </button>
        </>
      )}

      <EnhancedDialog isOpen={bulkEditorOpen} onClose={closeBulkEditor} title={`Edit ${count} submission${count === 1 ? '' : 's'}`} canOutsideClickClose={!savingBulkEdit}>
        <div className="sky-dialog-body submission-bulk-dialog">
          {!confirmingBulkEdit ? (
            <>
              <p>Apply one field change to every selected submission.</p>
              <label className="submission-bulk-field">
                Field
                <select value={selectedBulkField?.key || ''} onChange={(event) => resetBulkField(event.target.value as BulkFieldKey)}>
                  {bulkFields.map((field) => <option key={field.key} value={field.key}>{field.label}</option>)}
                </select>
              </label>
              {selectedBulkField && (
                <label className="submission-bulk-field">
                  Value
                  {selectedBulkField.input === 'picklist' && picklistValues.length > 0 ? (
                    <select value={bulkValue} disabled={clearValue} onChange={(event) => setBulkValue(event.target.value)}>
                      <option value="">Select {selectedBulkField.label.toLowerCase()}…</option>
                      {picklistValues.map((option) => <option key={option.value} value={option.value}>{option.label || option.value}</option>)}
                    </select>
                  ) : selectedBulkField.input === 'textarea' ? (
                    <textarea value={bulkValue} disabled={clearValue} onChange={(event) => setBulkValue(event.target.value)} placeholder={`Enter ${selectedBulkField.label.toLowerCase()}…`} rows={4} />
                  ) : (
                    <input type={selectedBulkField.input === 'number' ? 'number' : 'text'} min={selectedBulkField.min} step={selectedBulkField.step} value={bulkValue} disabled={clearValue} onChange={(event) => setBulkValue(event.target.value)} />
                  )}
                </label>
              )}
              {selectedFieldMetadata && !selectedFieldMetadata.is_required && (
                <label className="submission-bulk-clear"><input type="checkbox" checked={clearValue} onChange={(event) => setClearValue(event.target.checked)} /> Clear this value on all selected submissions</label>
              )}
            </>
          ) : (
            <div className="submission-bulk-confirmation">
              <p>This will update <strong>{count} submission{count === 1 ? '' : 's'}</strong>.</p>
              <dl><dt>Field</dt><dd>{selectedBulkField?.label}</dd><dt>New value</dt><dd>{clearValue ? 'Clear value' : bulkValue}</dd></dl>
              <p className="submission-bulk-warning">
                {undoable
                  ? 'This change is applied to every selected row. Undo next to the Edit button puts every row back to its previous value.'
                  : 'This change is applied to every selected row and cannot be undone from this dialog.'}
              </p>
            </div>
          )}
        </div>
        <div className="sky-dialog-footer">
          {confirmingBulkEdit ? (
            <>
              <button type="button" className="sky-btn sky-btn-ghost" disabled={savingBulkEdit} onClick={() => setConfirmingBulkEdit(false)}>Back</button>
              <button type="button" className="sky-btn sky-btn-primary" disabled={savingBulkEdit} onClick={() => void saveBulkEdit()}>{savingBulkEdit ? 'Saving…' : 'Confirm changes'}</button>
            </>
          ) : (
            <>
              <button type="button" className="sky-btn sky-btn-ghost" disabled={savingBulkEdit} onClick={closeBulkEditor}>Cancel</button>
              <button type="button" className="sky-btn sky-btn-primary" disabled={!selectedBulkField || savingBulkEdit} onClick={() => {
                const error = getBulkValueError();
                if (error) message.error(error);
                else setConfirmingBulkEdit(true);
              }}>Review changes</button>
            </>
          )}
        </div>
      </EnhancedDialog>
    </>
  );
}
